import { element, func, oneOfType, string } from 'prop-types';
import {
  forwardRef,
  useContext,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react';
import zoneContext from 'contexts/zone.js';
import useAPI from 'hooks/useAPI.js';
import { debug } from 'lib/debug';

const log = debug.extend('imperative-remote-control');

const ImperativeRemoteControl = forwardRef((props, forwardedRef) => {
  const { id, children } = props;
  const API = useAPI();
  const {
    hostApp: { createControl, removeControl },
  } = API;
  const hasReflection = useRef(false);
  const { zone_id } = useContext(zoneContext);

  const description = useMemo(() => {
    return {
      element_id: id,
      element_type: 'button',
      widget_type: 'proxy_button',
      zone_id,
    };
  }, [id, zone_id]);

  const [position, setPosition] = useState();

  const events = useMemo(() => {
    return {
      click: () => {
        props.onClick();
      },
      over: () => {
        props.onOver();
      },
      leave: () => {
        props.onLeave();
      },
    };
  }, [props]);

  useEffect(() => {
    if (position) {
      log('create ', id);
      createControl({
        element: {
          ...description,
          element_position: position,
        },
        events,
      });
      hasReflection.current = true;
    }

    return () => {
      if (hasReflection.current) {
        log('remove', id);
        removeControl({
          element: description,
        });
        hasReflection.current = false;
      }
    };
  }, [createControl, removeControl, description, events, id, position]);

  useImperativeHandle(
    forwardedRef,
    () => ({
      setPosition,
    }),
    [],
  );

  return children;
});

export { ImperativeRemoteControl };

ImperativeRemoteControl.propTypes = {
  id: string.isRequired,
  onOver: func,
  onLeave: func,
  onClick: func,
  children: oneOfType([element, string]),
};

ImperativeRemoteControl.defaultProps = {
  onOver: Function.prototype,
  onLeave: Function.prototype,
  onClick: Function.prototype,
};
